import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CheckIcon from '@mui/icons-material/Check';
import restaurantService from '../../services/restaurant.service';
import { TZ } from '../../utils/date';

const statusColors = {
  pending: 'warning',
  confirmed: 'info',
  preparing: 'info',
  ready: 'success',
  picked_up: 'primary',
  delivering: 'primary',
  delivered: 'success',
  collected: 'success',
  cancelled: 'error',
};

const getStatusLabel = (order) => {
  const isCAC = order.order_type === 'click_and_collect';
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: isCAC ? 'Prête à récupérer' : 'Prête',
    picked_up: 'Récupérée par livreur',
    delivering: 'En livraison',
    delivered: 'Livrée',
    collected: 'Récupérée',
    cancelled: 'Annulée',
  };
  return labels[order.order_status] || order.order_status;
};

const RestaurantOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await restaurantService.getMyOrders();
      setOrders(data.orders || []);
    } catch (err) {
      setError('Erreur lors du chargement des commandes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight={700}>
          Mes commandes restaurant
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Suivez l'état de vos commandes
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {orders.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <RestaurantIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucune commande
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Vous n'avez pas encore passé de commande
            </Typography>
            <Button component={Link} to="/restaurants" variant="contained">
              Découvrir les restaurants
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => (
            <Grid item xs={12} key={order.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="h6">
                              {order.restaurant_name || 'Restaurant'}
                            </Typography>
                            {order.order_type === 'click_and_collect' && (
                              <Chip
                                icon={<StorefrontIcon sx={{ fontSize: '14px !important' }} />}
                                label="Click & Collect"
                                size="small"
                                sx={{ bgcolor: 'rgba(85,37,131,0.1)', color: '#552583', border: '1px solid rgba(85,37,131,0.3)', height: 22, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Commande #{order.order_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', {
                              timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusLabel(order)}
                          color={statusColors[order.order_status] || 'default'}
                        />
                      </Box>

                      {/* Code de retrait Click & Collect */}
                      {order.order_type === 'click_and_collect' && order.order_status === 'ready' && order.delivery_code && (
                        <Box
                          sx={{
                            mb: 2, p: 2, borderRadius: 2,
                            background: 'linear-gradient(135deg, rgba(85,37,131,0.08), rgba(85,37,131,0.15))',
                            border: '1px solid rgba(85,37,131,0.25)',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#4a4a4a', display: 'block', mb: 0.5 }}>
                            Code de retrait — montrez-le au restaurant
                          </Typography>
                          <Typography variant="h4" fontWeight={800} sx={{ color: '#552583', letterSpacing: '0.3em' }}>
                            {order.delivery_code}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Box display="flex" gap={3} mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {order.estimated_delivery_time
                              ? new Date(order.estimated_delivery_time).toLocaleTimeString('fr-FR', {
                                  timeZone: TZ, hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'Bientôt'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {order.order_type === 'click_and_collect'
                            ? <StorefrontIcon fontSize="small" color="action" />
                            : <LocalShippingIcon fontSize="small" color="action" />
                          }
                          <Typography variant="body2">
                            {order.order_type === 'click_and_collect'
                              ? 'À emporter'
                              : `Livraison: ${order.delivery_fee} €`
                            }
                          </Typography>
                        </Box>
                      </Box>

                      {order.special_instructions && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(85,37,131,0.04)', borderRadius: 1, border: '1px solid rgba(85,37,131,0.1)' }}>
                          <Typography variant="caption" color="text.secondary">
                            Instructions:
                          </Typography>
                          <Typography variant="body2">
                            {order.special_instructions}
                          </Typography>
                        </Box>
                      )}

                      {['confirmed', 'preparing', 'ready', 'picked_up', 'delivering'].includes(order.order_status) && order.order_type !== 'click_and_collect' && (
                        <Button
                          component={Link}
                          to={`/restaurant/orders/${order.id}/tracking`}
                          variant="contained"
                          fullWidth
                          startIcon={<MyLocationIcon />}
                          sx={{
                            mt: 2,
                            py: 1.5,
                            background: 'linear-gradient(135deg, #2D4A35, #1E3525)',
                            fontWeight: 700,
                            boxShadow: '0 4px 20px rgba(85,37,131,0.3)',
                            animation: 'pulse-glow 2s ease-in-out infinite',
                            '@keyframes pulse-glow': {
                              '0%, 100%': { boxShadow: '0 4px 20px rgba(85,37,131,0.3)' },
                              '50%': { boxShadow: '0 4px 30px rgba(85,37,131,0.5)' },
                            },
                          }}
                        >
                          Suivre ma commande
                        </Button>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(85,37,131,0.04)',
                          borderRadius: 2,
                          border: '1px solid rgba(85,37,131,0.1)',
                          height: '100%',
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          Résumé
                        </Typography>
                        <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">Sous-total</Typography>
                          <Typography variant="body2">{order.subtotal} €</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            {order.order_type === 'click_and_collect' ? 'Retrait' : 'Livraison'}
                          </Typography>
                          <Typography variant="body2">
                            {order.delivery_fee === 0 || order.delivery_fee === '0.00' ? 'Gratuit' : `${order.delivery_fee} €`}
                          </Typography>
                        </Box>
                        {order.tax > 0 && (
                          <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="body2">Taxes</Typography>
                            <Typography variant="body2">{order.tax} €</Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle1" fontWeight={600}>
                            Total
                          </Typography>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#552583' }}>
                            {order.total_amount} €
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Chip
                            label={
                              order.payment_status === 'paid'
                                ? 'Payé'
                                : order.payment_status === 'pending'
                                ? 'En attente'
                                : 'Non payé'
                            }
                            color={order.payment_status === 'paid' ? 'success' : 'warning'}
                            size="small"
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {order.order_status === 'delivered' && order.delivered_at && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(34,197,94,0.08)', borderRadius: 1, border: '1px solid rgba(34,197,94,0.25)' }}>
                      <Typography variant="body2" sx={{ color: '#16a34a' }}>
                        <CheckIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />Livrée le{' '}
                        {new Date(order.delivered_at).toLocaleDateString('fr-FR', {
                          timeZone: TZ, day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default RestaurantOrders;
